import type {
  AdminExhibition,
  AdminExhibitionPurpose,
  ExhibitionFields,
} from '@repo/api-contracts';
import { type ReactElement, useEffect, useState } from 'react';
import { useExhibitionsQuery, useInvalidateExhibitions } from '@/entities/exhibition';
import { ExhibitionProductsModal } from '@/features/exhibition-products';
import {
  ExhibitionFormModal,
  ExhibitionRow,
  useCreateExhibition,
  useDeleteExhibition,
  useMoveExhibition,
  useSetExhibitionVisibility,
  useUpdateExhibition,
} from '@/features/exhibitions';
import { Button, EmptyState } from '@/shared/ui';

type ExhibitionForm =
  | { readonly mode: 'create' }
  | { readonly mode: 'edit'; readonly exhibition: AdminExhibition };

interface ExhibitionListSectionProps {
  /** 지금 고른 목적 탭 — 이 목적의 기획전만 보여 준다. */
  readonly purpose: AdminExhibitionPurpose;
  /** 폼 모달의 목적 select 가 쓰는 전체 목적. */
  readonly purposes: readonly AdminExhibitionPurpose[];
  /** 목적 탭 쪽 요청(추가·이름 변경·재배치·삭제)이 진행 중이면 이 화면의 버튼도 함께 잠근다. */
  readonly isPurposeBusy: boolean;
  /** 이 화면 자신의 요청 상태를 부모로 올려 준다 — 목적 탭도 함께 잠그게 한다. */
  readonly onBusyChange: (isBusy: boolean) => void;
}

/** 고른 목적의 기획전 목록 — 추가·수정·공개 전환·이동·상품 관리·삭제. */
export const ExhibitionListSection = ({
  purpose,
  purposes,
  isPurposeBusy,
  onBusyChange,
}: ExhibitionListSectionProps): ReactElement => {
  const exhibitionsQuery = useExhibitionsQuery(purpose.id);
  const { invalidateExhibitions, invalidateAll, invalidateExhibitionProducts } =
    useInvalidateExhibitions();
  const invalidateCurrent = (): void => invalidateExhibitions(purpose.id);

  // 추가도 폼의 목적 select 로 다른 목적을 골라 만들 수 있다 — 수정과 마찬가지로
  // 옛(현재) 목적·새 목적 두 목록이 모두 달라질 수 있어 전체를 무효화한다.
  const createAction = useCreateExhibition({ onSuccess: invalidateAll });
  const updateAction = useUpdateExhibition({ onSuccess: invalidateAll });
  const visibilityAction = useSetExhibitionVisibility({ onSuccess: invalidateCurrent });
  const moveAction = useMoveExhibition({ onSuccess: invalidateCurrent });
  const deleteAction = useDeleteExhibition({ onSuccess: invalidateCurrent });

  const [form, setForm] = useState<ExhibitionForm | null>(null);
  const [productsTarget, setProductsTarget] = useState<AdminExhibition | null>(null);

  const exhibitions = exhibitionsQuery.data ?? [];
  // 요청 하나가 진행 중이면 화면의 버튼을 모두 잠근다 — 순번이 같이 바뀌는 동안 꼬이지 않게.
  const isOwnBusy =
    createAction.isPending ||
    updateAction.isPending ||
    visibilityAction.isPending ||
    moveAction.isPending ||
    deleteAction.isPending;
  // 목적 탭 쪽 요청도 함께 본다 — 목적 삭제(CASCADE)가 진행 중인데 이 목록 버튼만 살아 있으면
  // 경합으로 "기획전을 찾을 수 없습니다" 를 받을 수 있다(§8-6).
  const isBusy = isOwnBusy || isPurposeBusy;

  useEffect(() => {
    onBusyChange(isOwnBusy);
    // 이 화면이 사라지면(예: 목적이 지워져 selectedPurpose 가 null 이 되면) 부모의 잠금도 함께 푼다 —
    // 아니면 목적 탭이 계속 잠긴 채로 남는다.
    return () => onBusyChange(false);
  }, [isOwnBusy, onBusyChange]);

  const listError =
    visibilityAction.error?.message ??
    moveAction.error?.message ??
    deleteAction.error?.message ??
    null;

  // 공개 전환·이동·삭제는 오류 한 줄(listError)을 함께 쓴다. 새 요청을 보내기 전에 세 요청의
  // 지난 오류를 모두 지운다 — 아니면 다른 요청을 해도 옛 오류가 그 요청이 다시 돌 때까지 남는다.
  const clearListErrors = (): void => {
    visibilityAction.reset();
    moveAction.reset();
    deleteAction.reset();
  };

  const handleToggleVisibility = (exhibition: AdminExhibition): void => {
    clearListErrors();
    visibilityAction.setVisibility(exhibition.id, !exhibition.isVisible);
  };

  const handleMove = (exhibition: AdminExhibition, direction: 'UP' | 'DOWN'): void => {
    clearListErrors();
    moveAction.moveExhibition(exhibition.id, direction);
  };

  // 모달을 열고 닫을 때 지난 업로드·서버 오류를 지운다 — 새 모달에 옛 오류가 남지 않게.
  const changeForm = (next: ExhibitionForm | null): void => {
    createAction.reset();
    updateAction.reset();
    setForm(next);
  };

  const handleSubmit = async (fields: ExhibitionFields, file: File | null): Promise<void> => {
    if (form === null) {
      return;
    }

    const isDone =
      form.mode === 'create'
        ? await createAction.createExhibition(fields, file)
        : await updateAction.updateExhibition(form.exhibition.id, fields, file);

    if (isDone) {
      changeForm(null);
    }
  };

  const handleDelete = (exhibition: AdminExhibition): void => {
    if (window.confirm(`기획전 "${exhibition.title}" 을(를) 삭제하시겠습니까?`)) {
      clearListErrors();
      deleteAction.deleteExhibition(exhibition.id);
    }
  };

  return (
    <div className="mt-6 border-t border-foreground/[0.06] pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          위에서부터 고객 기획전 페이지의 "{purpose.name}" 탭에 차례로 나옵니다.
        </p>
        <Button size="sm" disabled={isBusy} onClick={() => changeForm({ mode: 'create' })}>
          기획전 추가
        </Button>
      </div>

      {listError !== null ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {listError}
        </p>
      ) : null}

      {exhibitionsQuery.isPending ? (
        <div className="mt-6">
          <EmptyState>불러오는 중…</EmptyState>
        </div>
      ) : exhibitionsQuery.isError ? (
        <div className="mt-6">
          <EmptyState isError>
            기획전을 불러오지 못했습니다.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => void exhibitionsQuery.refetch()}
            >
              다시 시도
            </button>
          </EmptyState>
        </div>
      ) : exhibitions.length === 0 ? (
        <div className="mt-6">
          <EmptyState>등록된 기획전이 없습니다.</EmptyState>
        </div>
      ) : (
        <ul className="mt-6 flex flex-col gap-2">
          {exhibitions.map((exhibition, index) => (
            <ExhibitionRow
              key={exhibition.id}
              exhibition={exhibition}
              order={index + 1}
              isFirst={index === 0}
              isLast={index === exhibitions.length - 1}
              isBusy={isBusy}
              onMove={(direction) => handleMove(exhibition, direction)}
              onEdit={() => changeForm({ mode: 'edit', exhibition })}
              onToggleVisibility={() => handleToggleVisibility(exhibition)}
              onManageProducts={() => setProductsTarget(exhibition)}
              onDelete={() => handleDelete(exhibition)}
            />
          ))}
        </ul>
      )}

      {form !== null ? (
        <ExhibitionFormModal
          key={form.mode === 'edit' ? form.exhibition.id : 'create'}
          mode={form.mode}
          purposes={purposes}
          initialFields={
            form.mode === 'edit'
              ? {
                  purposeId: form.exhibition.purposeId,
                  title: form.exhibition.title,
                  leadTimeNote: form.exhibition.leadTimeNote,
                }
              : { purposeId: purpose.id, title: '', leadTimeNote: '' }
          }
          currentImageUrl={form.mode === 'edit' ? form.exhibition.imageUrl : null}
          isPending={form.mode === 'create' ? createAction.isPending : updateAction.isPending}
          errorMessage={
            form.mode === 'create' ? createAction.errorMessage : updateAction.errorMessage
          }
          onSubmit={(fields, file) => void handleSubmit(fields, file)}
          onClose={() => changeForm(null)}
        />
      ) : null}

      {productsTarget !== null ? (
        <ExhibitionProductsModal
          exhibition={productsTarget}
          onChanged={() => invalidateExhibitionProducts(productsTarget.id, purpose.id)}
          onClose={() => setProductsTarget(null)}
        />
      ) : null}
    </div>
  );
};
